/**
 * nav.ts — THE sidebar nav model (Story UI-U2, decision Valerio 2026-07-27).
 *
 * One exported config module consumed by AppShell + tests: three sections
 * (Arbeit / Übersicht / Einstellungen) per Sebastian's prototype IA.
 * Renewals + Brok stay ROUTABLE but are NOT sidebar entries
 * (prototype: «Renewals vorerst ausgeblendet»; Brok opens from Heute).
 *
 * [Source: brokly-design-reference/components/brokly/app-sidebar.tsx] (IA + icons)
 * [Source: docs/design/2026-07-10_sebastian-package/design.md §5] (sidebar contract)
 * [Source: docs/stories/ui-u2-ia-navigation.md AC 1]
 * [Source: docs/concept/full/M20 - Version 3.0 Navigationskonzept Weblösung Broker.md §2.2]
 *   (Posteingang = its own Bereich, directly after Aufgaben)
 */

import type { ComponentType } from 'react';
import {
  Building2,
  CalendarDays,
  ChartPie,
  Coins,
  FolderOpen,
  Inbox,
  ListTodo,
  Megaphone,
  MessageSquare,
  ReceiptText,
  Settings,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { KOMMUNIKATION_ENABLED } from '../../lib/feature-flags';

/** Every in-app top-level route the shell knows (nav entries + off-sidebar routes). */
export type RoutePath =
  | '/'
  | '/kommunikation'
  | '/aufgaben'
  | '/posteingang'
  | '/rechnungen'
  | '/courtage'
  | '/portfolio'
  | '/kunden'
  | '/policen'
  // P2 Placement: the firm-wide Ausschreibungen list (the detail /ausschreibungen/$id keeps it active).
  | '/ausschreibungen'
  | '/versicherer'
  | '/ablage'
  | '/admin'
  | '/renewals'
  | '/brok'
  // Part C: the user-settings page — reached from the bottom-left profile area,
  // NOT a sidebar entry (so it stays out of NAV_SECTIONS).
  | '/einstellungen';

/**
 * Keys of nav items that MAY carry a live count badge (AC 3).
 * Courtage: no endpoint yet → no key → never a badge.
 */
export type BadgeKey = 'kommunikation' | 'aufgaben' | 'rechnungen' | 'posteingang';

export interface NavItem {
  to: RoutePath;
  label: string;
  icon: ComponentType<{ className?: string }>;
  badgeKey?: BadgeKey;
}

export interface NavSection {
  title: string;
  items: NavItem[];
}

/** The designed IA — exactly these sections/entries (Story UI-U2 AC 1). */
export const NAV_SECTIONS: NavSection[] = [
  {
    title: 'Arbeit',
    items: [
      { to: '/', label: 'Heute', icon: CalendarDays },
      // Disconnected while the Posteingang is the single inbox (lib/feature-flags.ts).
      ...(KOMMUNIKATION_ENABLED
        ? [{ to: '/kommunikation', label: 'Kommunikation', icon: MessageSquare, badgeKey: 'kommunikation' } as NavItem]
        : []),
      { to: '/aufgaben', label: 'Aufgaben', icon: ListTodo, badgeKey: 'aufgaben' },
      { to: '/posteingang', label: 'Posteingang', icon: Inbox, badgeKey: 'posteingang' },
      { to: '/rechnungen', label: 'Rechnungskontrolle', icon: ReceiptText, badgeKey: 'rechnungen' },
      { to: '/courtage', label: 'Courtage', icon: Coins },
    ],
  },
  {
    title: 'Übersicht',
    items: [
      { to: '/portfolio', label: 'Portfolio', icon: ChartPie },
      { to: '/kunden', label: 'Kunden', icon: Users },
      { to: '/policen', label: 'Policen', icon: ShieldCheck },
      // P2 Placement: every Ausschreibung of the firm (the per-customer view stays the Kunde tab).
      { to: '/ausschreibungen', label: 'Ausschreibungen', icon: Megaphone },
      { to: '/versicherer', label: 'Versicherer', icon: Building2 },
      // DMS M11: the Ablage grid — every document across Policen/Kunden/Schäden/
      // Rechnungen/Kommunikation. `isActive` (startsWith) keeps this entry active
      // on the inline-viewer detail route (/ablage/$id) too.
      { to: '/ablage', label: 'Ablage', icon: FolderOpen },
    ],
  },
  {
    title: 'Einstellungen',
    items: [
      // Entry only — the Administration AREA (own settings sidebar, /admin/$section,
      // permission gating) is Story UI-U6's scope. Visibility unconditional until
      // U6's effective-permissions gate lands.
      { to: '/admin', label: 'Administration', icon: Settings },
    ],
  },
];

/**
 * Active-state derivation: parent stays active on detail routes
 * (`/kunden/$id` → Kunden active). `/` matches only exactly.
 */
export const isActive = (to: string, path: string): boolean =>
  to === '/' ? path === '/' : path.startsWith(to);

/** Routable-but-off-sidebar routes still need a topbar title. */
const OFF_NAV_TITLES: ReadonlyArray<readonly [prefix: string, title: string]> = [
  ['/brok', 'Brok'],
  ['/renewals', 'Renewals'],
  ['/einstellungen', 'Einstellungen'],
  // /ausschreibungen(+$id) needs no row here any more: «Ausschreibungen» is a sidebar entry now,
  // so the detail route's title comes from the nav label via the prefix match below.
];

/** Topbar title for the current location (nav labels + off-sidebar routes). */
export function titleFor(path: string): string {
  for (const [prefix, title] of OFF_NAV_TITLES) {
    if (isActive(prefix, path)) return title;
  }
  return (
    NAV_SECTIONS.flatMap((s) => s.items).find((i) => isActive(i.to, path))?.label ?? 'Heute'
  );
}
